'use client';

/**
 * GroupOptionCard — UNA card para los 3 niveles de combo de grupo
 * (nombradas / mixta / solo por asignar). Reemplaza a RecommendedGroupCard
 * (spec §4.0 del plan: header 3 líneas, filas con foto Y estado textual,
 * panel derecho blanco con total + depósito + dos CTAs).
 */
import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Building2, Sparkles } from 'lucide-react';
import { companyConfig } from '@/lib/config-loader';
import { versionedImageUrl } from '@/lib/image-version';
import { trackPostHogEvent, type CTAEventProps } from '@/lib/analytics/posthog';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import { formatMoney } from '@/components/apartments/card/apartment-card-utils';
import type { GroupOptionDTO, GroupOptionLineDTO } from '@/lib/assignment/quote';

const BADGE_BY_KIND: Record<GroupOptionDTO['kind'], string> = {
  nombradas: 'Opción recomendada',
  mixta: 'Combinando lo disponible',
  economica: 'La más económica',
  compacta: 'El grupo más junto',
  homogenea: 'Todos iguales',
};

const VISIBLE_ROWS = 3;

interface GroupOptionCardProps {
  option: GroupOptionDTO;
  arrival: string;
  departure: string;
  ctaAttribution: CTAEventProps;
  onReserve: (option: GroupOptionDTO) => void;
}

function lineStateText(line: GroupOptionLineDTO): string {
  if (line.kind === 'named') {
    const parts = ['Unidad confirmada'];
    if (typeof line.guests === 'number') parts.push(`${line.guests} ${line.guests === 1 ? 'persona' : 'personas'}`);
    if (typeof line.piso === 'number' && line.piso > 0) parts.push(`Piso ${line.piso}`);
    return parts.join(' · ');
  }
  return `Por asignar · hasta ${line.capacityPerUnit} personas`;
}

function LineRow({ line, arrival, departure }: { line: GroupOptionLineDTO; arrival: string; departure: string }) {
  const name = line.units > 1 ? `${line.units} × ${line.displayName}` : line.displayName;
  const totalLine = line.totalPerUnit * line.units;
  const body = (
    <>
      <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-stone-100">
        {line.photoUrl ? (
          <Image
            src={versionedImageUrl(line.photoUrl)}
            alt={line.displayName}
            fill
            sizes="56px"
            loading="lazy"
            className="object-cover"
            quality={70}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-stone-400">
            <Building2 className="h-6 w-6" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 line-clamp-2">{name}</p>
        <p className="text-xs text-gray-600">{lineStateText(line)}</p>
      </div>
      <span className="flex-shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
        {formatMoney(totalLine)}
      </span>
    </>
  );

  // Filas nombradas clicables a su ficha (nueva pestaña, foco visible) —
  // sin links estirados que tapen botones.
  if (line.kind === 'named' && line.apartmentId) {
    const qs = new URLSearchParams();
    if (arrival) qs.set('checkIn', arrival);
    if (departure) qs.set('checkOut', departure);
    return (
      <Link
        href={`/apartamentos/${line.apartmentId}?${qs.toString()}`}
        target="_blank"
        rel="noopener noreferrer"
        prefetch={false}
        aria-label={`Ver ficha de ${line.displayName} (nueva pestaña)`}
        className="flex items-center gap-3 rounded-xl bg-white p-2.5 ring-1 ring-emerald-100 transition-colors hover:ring-emerald-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        {body}
      </Link>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white p-2.5 ring-1 ring-emerald-100">
      {body}
    </div>
  );
}

export default function GroupOptionCard({
  option,
  arrival,
  departure,
  ctaAttribution,
  onReserve,
}: GroupOptionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hiddenRows = option.lines.length - VISIBLE_ROWS;
  const visibleLines = expanded ? option.lines : option.lines.slice(0, VISIBLE_ROWS);

  const whatsappMessage = useMemo(() => {
    const lineSummary = option.lines
      .map((l) => `${l.units} × ${l.displayName}`)
      .join(' + ');
    return (
      `Hola, quiero cotizar ${option.totalUnits} apartamentos para mi grupo de ${option.people} personas ` +
      `del ${arrival} al ${departure}: ${lineSummary}. ` +
      `Total cotizado: ${formatMoney(option.totalCop)} COP por ${option.nights} ${option.nights === 1 ? 'noche' : 'noches'}.`
    );
  }, [option, arrival, departure]);

  const whatsappHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(whatsappMessage)}`,
    [whatsappMessage]
  );

  const handleReserve = () => {
    trackPostHogEvent('assignment_group_cta_clicked', {
      ...ctaAttribution,
      combo_kind: option.kind,
      total_units: option.totalUnits,
      total_cop: option.totalCop,
      deposit_cop: option.depositCop,
      people: option.people,
      nights: option.nights,
      cta_position: 'assignment_group',
      cta_label: 'Reservar',
    });
    onReserve(option);
  };

  return (
    <article className="overflow-hidden rounded-3xl border border-emerald-200 bg-emerald-50/70">
      <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[1.1fr_0.9fr] lg:gap-6">
        {/* ── Columna izquierda: header 3 líneas + filas ── */}
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 px-3 py-1 text-xs font-semibold text-white">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
            {BADGE_BY_KIND[option.kind]}
          </span>
          <h4 className="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
            {option.totalUnits} apartamentos para tu grupo de {option.people}
          </h4>
          <p className="mt-0.5 text-sm text-gray-600">
            Mismo edificio{option.floorSpreadClose ? ' · A pocos pisos' : ''}
          </p>

          <div className="mt-3 space-y-2">
            {visibleLines.map((line, i) => (
              <LineRow key={`${line.kind}-${line.productSlug ?? line.apartmentId ?? i}`} line={line} arrival={arrival} departure={departure} />
            ))}
          </div>
          {hiddenRows > 0 && !expanded ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-expanded={expanded}
              className="mt-2 text-sm font-semibold text-emerald-800 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
            >
              Ver {hiddenRows} {hiddenRows === 1 ? 'apartamento más' : 'apartamentos más'}
            </button>
          ) : null}
        </div>

        {/* ── Panel derecho (blanco): total + depósito + CTAs ── */}
        <div className="flex flex-col justify-between rounded-2xl bg-white p-4 sm:p-5">
          <div>
            <p className="text-sm font-medium text-gray-600">
              Total por {option.nights} {option.nights === 1 ? 'noche' : 'noches'}
            </p>
            <p className="text-2xl font-semibold tracking-tight text-gray-900 tabular-nums">
              {formatMoney(option.totalCop)}
            </p>
            <p className="text-xs text-gray-500">
              ≈ {formatMoney(option.perPersonNight)} por persona/noche
            </p>
            <p className="mt-3 text-sm text-gray-600">
              Reserva con solo{' '}
              <span className="font-semibold text-gray-950 tabular-nums">
                {formatMoney(option.depositCop)}
              </span>
            </p>
            <p className="text-xs text-gray-500">(una noche por apartamento)</p>
          </div>

          <div className="mt-4">
            <button
              type="button"
              onClick={handleReserve}
              className="flex w-full min-h-[44px] items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-px hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              Reservar
            </button>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                openTrackedWhatsApp(whatsappMessage, {
                  ...ctaAttribution,
                  guests_total: option.people,
                  result_count: option.totalUnits,
                  cta_position: 'assignment_group',
                  cta_label: `Cotizar por WhatsApp (${option.kind})`,
                  has_pricing: true,
                });
              }}
              className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 py-2 text-sm font-semibold text-gray-900 transition-colors hover:text-emerald-800"
            >
              <span>Cotizar por WhatsApp</span>
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
